import DuplicateConnection from 'components/DuplicateConnection';
import { useToast } from 'contexts/ToastContext';
import { useUser } from 'contexts/UserContext';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux.toolkit/hooks';
import {
  addInviteeInvitation,
  clearInviteeInvitations,
  removeInviteeInvitation,
  setInviteeInvitations,
} from 'redux.toolkit/state/invitation/reducers';
import {
  addPublicLobbyRound,
  clearPublicLobbyRounds,
  removePublicLobbyRound,
  setPublicLobbyRounds,
  upsertPublicLobbyRound,
} from 'redux.toolkit/state/lobby/reducers';
import {
  setNullRound,
  setNullTempRound,
} from 'redux.toolkit/state/round/reducers';
import {
  setActiveUser,
  setOnlineCount,
} from 'redux.toolkit/state/user/reducers';
import { selectUserState } from 'redux.toolkit/state/user/selectors';
import { Socket } from 'socket.io-client';
import { ErrorMessages, ErrorMessagesType } from 'types/Error';
import { InvitationResolutionReason } from 'types/Invitation';
import { IRound } from 'types/Round';
import { RoutePaths } from 'types/Routes';
import { IUserPresence } from 'types/UserPresence';
import { isFaritanyRound, isPublicWaitingRound } from 'utils/publicLobbyRounds';
import { createInstanceSocket, disconnectSocket } from './socket';
import { SOCKET_IN, SOCKET_OUT } from './socketEvents';

interface SocketContextValue {
  socket: Socket | null;
  connected: boolean;
  reconnecting: boolean;
}

const SocketContext = createContext<SocketContextValue>({
  socket: null,
  connected: false,
  reconnecting: false,
});

export const useSocket = (): SocketContextValue => useContext(SocketContext);

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const { token } = useAppSelector(selectUserState);
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const userRef = useRef(user);
  userRef.current = user;
  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;

  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState<boolean>(false);
  const [reconnecting, setReconnecting] = useState<boolean>(false);
  const [duplicate, setDuplicate] = useState<boolean>(false);

  const disconnectToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const clearDisconnectToastTimer = () => {
    if (disconnectToastTimerRef.current != null) {
      clearTimeout(disconnectToastTimerRef.current);
      disconnectToastTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (!token) {
      disconnectSocket();
      setSocket(null);
      setConnected(false);
      dispatch(clearInviteeInvitations());
      dispatch(clearPublicLobbyRounds());
      dispatch(setActiveUser([]));
      dispatch(setOnlineCount(0));
      return;
    }

    const instance = createInstanceSocket(token);
    setSocket(instance);

    const syncPendingInvitations = () => {
      instance.emit(SOCKET_OUT.SYNC_PENDING_INVITATIONS);
    };

    const handleConnect = () => {
      clearDisconnectToastTimer();
      setConnected(true);
      setReconnecting(false);
      // Bootstrap centralisé : un seul emit par session, plus besoin de le faire dans chaque page.
      instance.emit(SOCKET_OUT.IS_IN_ROOM_IN_PROGRESS);
      instance.emit(SOCKET_OUT.FETCH_ROUNDS);
      instance.emit(SOCKET_OUT.JOIN_LOBBY);
      syncPendingInvitations();
      // Resync compteur présence après reconnexion (au cas où un broadcast a été manqué).
      instance.emit(SOCKET_OUT.COUNT_USER_CONNECTED);
    };

    const handleInvitation = (round: IRound) => {
      const u = userRef.current;
      if (!u?.id || !round.roomName || !round.invitationFor) return;
      if (u.id !== round.invitationFor.id) return;
      if (!isFaritanyRound(round)) return;

      dispatch(addInviteeInvitation(round));

      const pseudo = round.creator?.pseudo ?? 'un joueur';
      const onPrivateTab = locationRef.current.startsWith('/active-user');
      showToast(
        onPrivateTab
          ? `Nouvelle invitation de ${pseudo}`
          : `Nouvelle invitation de ${pseudo}. Voir l’onglet Privé.`,
        'info',
        4000,
      );
    };

    const handlePendingInvitations = (rounds: IRound[]) => {
      const u = userRef.current;
      if (!u?.id || !Array.isArray(rounds)) return;

      const forInvitee = rounds.filter(
        (r) => r.invitationFor?.id === u.id && isFaritanyRound(r),
      );
      dispatch(setInviteeInvitations(forInvitee));
    };

    const handleRejectedInvitation = (payload: {
      roomName: string;
      round?: IRound;
      reason?: InvitationResolutionReason;
    }) => {
      if (payload?.roomName) {
        dispatch(removeInviteeInvitation(payload.roomName));
      }

      const u = userRef.current;
      const inviteeId = payload.round?.invitationFor?.id;
      if (!u?.id || inviteeId !== u.id || !payload.reason) return;

      switch (payload.reason) {
        case 'INVITATION_CANCELLED':
          showToast("Le créateur a annulé l'invitation.", 'info', 4000);
          break;
        case 'EXPIRED':
          showToast('Invitation expirée.', 'info', 4000);
          break;
        case 'SUPERSEDED_BY_OTHER_ACCEPT':
          // L'invitation est retirée silencieusement de la liste (dispatch ci-dessus).
          // Pas de toast pour l'invité — seul le créateur qui attendait en a besoin.
          break;
        default:
          break;
      }
    };

    // En pleine partie, les micro-coupures sont fréquentes et le badge
    // ConnectionStatus suffit : on évite le bruit « perdue / rétablie » à chaque cycle.
    const isInGameScreen = () => locationRef.current.startsWith('/faritany/');

    const handleDisconnect = () => {
      setConnected(false);
      clearDisconnectToastTimer();
      if (isInGameScreen()) return;
      disconnectToastTimerRef.current = setTimeout(() => {
        disconnectToastTimerRef.current = null;
        showToast('Connexion perdue, reconnexion en cours...', 'info', 4000);
      }, 2500);
    };

    const handleReconnectAttempt = () => setReconnecting(true);
    const handleReconnect = () => {
      setReconnecting(false);
      const wasToastPending = disconnectToastTimerRef.current != null;
      clearDisconnectToastTimer();
      if (isInGameScreen()) return;
      if (wasToastPending) return;
      showToast('Connexion rétablie', 'info', 3000);
    };
    const handleReconnectFailed = () => {
      setReconnecting(false);
      clearDisconnectToastTimer();
      showToast(
        'Impossible de se reconnecter au serveur. Vérifiez votre connexion.',
        'error',
        5000,
      );
    };

    const handleError = (value: keyof ErrorMessagesType) => {
      const message = ErrorMessages[value];
      if (
        message === ErrorMessages.PARTIE_NON_TROUVÉE ||
        message === ErrorMessages.CLIENT_NOT_IN_ROOM ||
        message === ErrorMessages.ACCESS_DENIED_ROOM
      ) {
        dispatch(setNullRound());
        dispatch(setNullTempRound());
        navigate(RoutePaths.ROUND, { replace: true });
      }
      showToast(message ?? 'Une erreur est survenue', 'error', 3000);
    };

    const handleDuplicate = () => setDuplicate(true);

    const handleUserConnected = (users: IUserPresence[] | number) => {
      // Compat héritage : si jamais le serveur émet encore un nombre sur
      // `userConnected`, on le réoriente vers le compteur. Le pipeline normal
      // utilise désormais `userConnectedCount` pour cette information.
      if (Array.isArray(users)) {
        dispatch(setActiveUser(users));
        dispatch(setOnlineCount(users.length));
      } else if (typeof users === 'number') {
        dispatch(setOnlineCount(users));
      }
    };

    const handleUserConnectedCount = (count: number) => {
      if (typeof count === 'number') dispatch(setOnlineCount(count));
    };

    const handleRoundsFetched = (rounds: IRound[]) => {
      if (!Array.isArray(rounds)) return;
      dispatch(setPublicLobbyRounds(rounds));
    };

    const handleNewRoundCreated = (round: IRound) => {
      if (!round?.roomName || !isPublicWaitingRound(round)) return;
      dispatch(addPublicLobbyRound(round));
    };

    const handleRoundCreated = (round: IRound) => {
      if (!round?.roomName || !isPublicWaitingRound(round)) return;
      dispatch(addPublicLobbyRound(round));
    };

    const handleRoundsToDelete = (roomName: string) => {
      if (roomName) dispatch(removePublicLobbyRound(roomName));
    };

    const handleRoundsToUpdate = (round: IRound) => {
      if (!round?.roomName) return;
      dispatch(upsertPublicLobbyRound(round));
    };

    instance.on(SOCKET_IN.CONNECT, handleConnect);
    instance.on(SOCKET_IN.DISCONNECT, handleDisconnect);
    // reconnect / reconnect_attempt / reconnect_failed sont émis par le Manager
    // (instance.io), pas par le Socket lui-même — s'abonner sur `instance`
    // directement ne déclenche jamais ces handlers.
    instance.io.on(SOCKET_IN.RECONNECT_ATTEMPT, handleReconnectAttempt);
    instance.io.on(SOCKET_IN.RECONNECT, handleReconnect);
    instance.io.on(SOCKET_IN.RECONNECT_FAILED, handleReconnectFailed);
    instance.on(SOCKET_IN.ERROR, handleError);
    instance.on(SOCKET_IN.DUPLICATE_CONNECTION, handleDuplicate);
    instance.on(SOCKET_IN.USER_CONNECTED, handleUserConnected);
    instance.on(SOCKET_IN.USER_CONNECTED_COUNT, handleUserConnectedCount);
    instance.on(SOCKET_IN.INVITATION, handleInvitation);
    instance.on(SOCKET_IN.PENDING_INVITATIONS, handlePendingInvitations);
    instance.on(SOCKET_IN.REJECTED_INVITATION, handleRejectedInvitation);
    instance.on(SOCKET_IN.ROUNDS_FETCHED, handleRoundsFetched);
    instance.on(SOCKET_IN.NEW_ROUND_CREATED, handleNewRoundCreated);
    instance.on(SOCKET_IN.ROUND_CREATED, handleRoundCreated);
    instance.on(SOCKET_IN.ROUNDS_TO_DELETE, handleRoundsToDelete);
    instance.on(SOCKET_IN.ROUNDS_TO_UPDATE, handleRoundsToUpdate);

    // Au retour au premier plan, le throttling des timers pendant que l'onglet
    // était en arrière-plan peut retarder le cycle de reconnexion interne de
    // Socket.IO. `instance.connect()` seul est un no-op tant que le Manager est
    // déjà en train de retenter (`io._reconnecting === true`, le cas quasi
    // permanent ici vu reconnectionAttempts: Infinity) : il n'annule jamais le
    // backoff throttlé. On passe donc par le Manager pour annuler proprement le
    // cycle en attente (reset du backoff, sans laisser de timer résiduel) avant
    // de rouvrir immédiatement une connexion.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || instance.connected) return;
      // `.disconnect()` fait le même travail mais est typé `private` dans les
      // définitions socket.io-client ; `_close()` est l'alias public équivalent.
      instance.io._close();
      instance.connect();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearDisconnectToastTimer();
      instance.off(SOCKET_IN.CONNECT, handleConnect);
      instance.off(SOCKET_IN.DISCONNECT, handleDisconnect);
      instance.io.off(SOCKET_IN.RECONNECT_ATTEMPT, handleReconnectAttempt);
      instance.io.off(SOCKET_IN.RECONNECT, handleReconnect);
      instance.io.off(SOCKET_IN.RECONNECT_FAILED, handleReconnectFailed);
      instance.off(SOCKET_IN.ERROR, handleError);
      instance.off(SOCKET_IN.DUPLICATE_CONNECTION, handleDuplicate);
      instance.off(SOCKET_IN.USER_CONNECTED, handleUserConnected);
      instance.off(SOCKET_IN.USER_CONNECTED_COUNT, handleUserConnectedCount);
      instance.off(SOCKET_IN.INVITATION, handleInvitation);
      instance.off(SOCKET_IN.PENDING_INVITATIONS, handlePendingInvitations);
      instance.off(SOCKET_IN.REJECTED_INVITATION, handleRejectedInvitation);
      instance.off(SOCKET_IN.ROUNDS_FETCHED, handleRoundsFetched);
      instance.off(SOCKET_IN.NEW_ROUND_CREATED, handleNewRoundCreated);
      instance.off(SOCKET_IN.ROUND_CREATED, handleRoundCreated);
      instance.off(SOCKET_IN.ROUNDS_TO_DELETE, handleRoundsToDelete);
      instance.off(SOCKET_IN.ROUNDS_TO_UPDATE, handleRoundsToUpdate);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      disconnectSocket();
      setSocket(null);
      setConnected(false);
    };
    // showToast / navigate / dispatch sont stables ; on ré-instancie uniquement quand le token change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const value = useMemo(
    () => ({ socket, connected, reconnecting }),
    [socket, connected, reconnecting],
  );

  return (
    <SocketContext.Provider value={value}>
      <DuplicateConnection isOpen={duplicate} setIsModalOpen={setDuplicate} />
      {children}
    </SocketContext.Provider>
  );
};
